<template>
  <div>
    <Order :order="order" />
    <v-dialog
      v-model="holdDialog"
      max-width="600"
    >
      <div>
        <VWidget :title="'Set order ' + order.number + ' status to on hold'">
          <v-layout
            slot="widget-content"
            row
            wrap
          >
            <v-flex
              xs12
              mb-2
            >
              {{ $t('Please write the hold reason:') }}
            </v-flex>
            <v-text-field
              v-model="holdReason"
              :label="$t('Reason')"
              xs12
              outline
            />
          </v-layout>
        </VWidget>
        <v-card>
          <v-card-actions>
            <v-spacer />
            <v-btn
              class="primary"
              flat
              small
              @click="setAsHold()"
            >
              {{ $t('Put on hold') }}
            </v-btn>
            <v-btn
              color="darken-1"
              flat
              small
              @click="holdDialog=false"
            >
              {{ $t('Close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import Order from '../components/OrderSingle'
import OrderService from '../service'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'OrderPage',

  components: {
    Order
  },

  actionBar () {
    return {
      title: 'Order',
      actions: [
        {
          text: 'Hold',
          hidden: true,
          event: 'hold'
        },
        {
          text: 'Release',
          hidden: true,
          event: 'release'
        },
        {
          color: 'error',
          text: 'Cancel',
          hidden: true,
          event: 'cancel'
        },
        {
          text: 'Archive',
          color: 'success',
          hidden: true,
          event: 'archive'
        }
      ]
    }
  },

  data () {
    return {
      order: {},
      holdDialog: false,
      holdReason: '',
      renewEvents: [
        'payment-captured',
        'payment-voided',
        'order-marked-as-paid',
        'order-refunded',
        'item-saved',
        'renew-order'
      ]
    }
  },

  computed: {
    ...mapGetters(['orderStateMachine']),

    holdActionStates () {
      return {
        disabled: !this.order || this.order.inProgress,
        loading: !this.order || this.order.inProgress,
        hidden: !this.order || this.cannot('hold')
      }
    },

    releaseActionStates () {
      return {
        disabled: !this.order || this.order.inProgress,
        loading: !this.order || this.order.inProgress,
        hidden: !this.order || this.cannot('release')
      }
    },

    cancelActionStates () {
      return {
        disabled: !this.order || this.order.inProgress,
        loading: !this.order || this.order.inProgress,
        hidden: !this.order || this.cannot('cancel')
      }
    },

    archiveActionStates () {
      return {
        disabled: !this.order || this.order.inProgress,
        loading: !this.order || this.order.inProgress,
        hidden: !this.order || this.cannot('archive')
      }
    }
  },

  async mounted () {
    try {
      this.setOrderStateMachine(await this.$http.make('state_machine').getDefault())

      this.order = await OrderService.loadOrder(this.$route.params.id)
      this.$radio.$emit('action-panel-title', `Order #${this.order.number}`)
      this.registerEventListeners()

      await this.checkRequestedDelivery()
    } catch (e) {
      this.$toast.error(this.$t(e.message))
    }
  },

  destroyed () {
    for (const event of this.renewEvents) {
      this.$radio.$off(event)
    }
  },

  methods: {
    ...mapActions(['setOrderStateMachine']),

    cannot (action) {
      return this.orderStateMachine && this.order && this.orderStateMachine.cannot(action, this.order.status)
    },

    async onActionRelease () {
      try {
        await this.order.release()
        await this.renew()
      } catch (e) {
        this.$toast.error(this.$t(e.message))
      }
      this.$vuetify.goTo(0)
    },

    async onActionHold () {
      this.holdReason = ''
      this.holdDialog = true
    },

    async setAsHold () {
      try {
        await this.order.hold(`Hold reason: ${this.holdReason}`)
        await this.renew()
        this.holdDialog = false
      } catch (e) {
        this.holdDialog = false
        this.$toast.error(this.$t(e.message))
      }
      this.$vuetify.goTo(0)
    },

    async onActionCancel () {
      if (!await this.$root.$confirm(this.$t('Danger Zone'), this.$t('Please Confirm'), { color: 'red' })) {
        return
      }
      try {
        await this.order.cancel()
        await this.renew()
      } catch (e) {
        this.$toast.error(this.$t(e.message))
      }
      this.$vuetify.goTo(0)
    },

    async onActionArchive () {
      try {
        await this.order.archive()
        await this.renew()
      } catch (e) {
        this.$toast.error(this.$t(e.message))
      }
      this.$vuetify.goTo(0)
    },

    async renew () {
      let self = this
      const order = await OrderService.loadOrder(this.order.number)
      this.order = this.$set(self, 'order', order)
    },

    async registerEventListeners () {
      let self = this
      for (const event of this.renewEvents) {
        this.$radio.$on(event, await self.renew)
      }
    },

    async checkRequestedDelivery () {
      if (!this.order.requested_delivery || this.order.unfulfilled.length === 0) {
        return
      }

      const response = await this.$http.make('settings').find('general/order')

      if (!response.requested_delivery_window) {
        return
      }

      const requestedDate = new Date(this.order.requested_delivery.split(' ').shift())
      const currentDate = new Date()
      const timeDiff = requestedDate.getTime() - currentDate.getTime()
      const daysDiff = timeDiff / (1000 * 60 * 60 * 24)
      if (daysDiff > 0 && daysDiff <= response.requested_delivery_window) {
        this.$toast.warning(this.$t('The requested delivery date for the order is approaching.'))
      }
    }
  }
}
</script>
